import { FC, MouseEvent, ReactNode } from 'react'
import { Link as RouterLink, useLocation } from 'react-router-dom'
import {
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Tooltip,
} from '@mui/material'
import { theme } from '@/theme'

type CustomLinkNavigationProps = {
  icon: ReactNode
  label: string
  link: string
  isExternal?: boolean
  /** If isExternal is true, open in a new tab/window. Default: true for external links, can be overridden to false. */
  openInNewTab?: boolean
  primaryColor?: boolean
  isCollapsed?: boolean
  onClick?: never
}

type CustomLinkActionProps = {
  icon: ReactNode
  label: string
  link?: never
  isExternal?: boolean
  openInNewTab?: boolean
  primaryColor?: boolean
  isCollapsed?: boolean
  onClick: () => void
}

type CustomLinkProps = CustomLinkNavigationProps | CustomLinkActionProps

export const CustomLink: FC<CustomLinkProps> = ({
  icon,
  label,
  link,
  isExternal,
  openInNewTab: openInNewTabProp,
  primaryColor,
  isCollapsed = false,
  onClick,
}) => {
  const location = useLocation()
  // Only attempt to mark active for internal (router) links
  // Check if current pathname matches or starts with the link
  const currentPath = location.pathname
  const isActive =
    !!link &&
    !isExternal &&
    (currentPath === link ||
      currentPath.startsWith(link + '/') ||
      (link.startsWith('/dashboard') && currentPath.startsWith('/dashboard')))

  const handleClick = (
    event: MouseEvent<HTMLButtonElement> | MouseEvent<HTMLAnchorElement>,
  ) => {
    if (!link && onClick) {
      event.preventDefault()
      onClick()
    }
  }

  const colorStyle = primaryColor
    ? {
        color: 'white',
        '&:hover': {
          color: 'white',
        },
      }
    : { color: isActive ? 'primary.main' : 'grey.600' }

  // Decide which underlying component to use:
  // - Internal navigation: RouterLink
  // - External URL: 'a'
  // - Action (no link): 'button'
  const componentType: any = link ? (isExternal ? 'a' : RouterLink) : 'button'

  // Determine if we should open in new tab (defaults to true for external links unless explicitly set false)
  const shouldOpenInNewTab = !!isExternal && (openInNewTabProp ?? true)

  const linkButton = (
    <ListItemButton
      component={componentType}
      {...(!isExternal && link ? { to: link } : {})}
      {...(isExternal && link ? { href: link } : {})}
      {...(isExternal && link && shouldOpenInNewTab
        ? { target: '_blank', rel: 'noopener noreferrer' }
        : {})}
      // Open external links in same window (no target _blank)
      sx={{
        minHeight: 48,
        justifyContent: 'initial',
        px: 2.5,
        py: 0.75,
        borderRadius: 1,
        mx: 0.5,
        '&.Mui-selected': {
          backgroundColor: 'rgba(61, 90, 254, 0.08)',
          '&:hover': {
            backgroundColor: 'rgba(61, 90, 254, 0.12)',
          },
        },
        ...(primaryColor && {
          bgcolor: theme.palette.primary.main,
          '&:hover': {
            bgcolor: theme.palette.primary.main,
          },
        }),
      }}
      selected={isActive}
      onClick={handleClick}
    >
      <ListItemIcon
        sx={{
          minWidth: 0,
          mr: isCollapsed ? 0 : 3,
          justifyContent: 'center',
          ...colorStyle,
        }}
      >
        {icon}
      </ListItemIcon>
      {!isCollapsed && (
        <ListItemText
          primary={label}
          slotProps={{
            primary: {
              fontSize: '0.875rem',
              fontWeight: 700,
              fontFamily: 'Mulish, sans-serif',
              ...colorStyle,
            },
          }}
        />
      )}
    </ListItemButton>
  )

  return (
    <ListItem disablePadding>
      {isCollapsed ? (
        <Tooltip title={label} placement="right">
          {linkButton}
        </Tooltip>
      ) : (
        linkButton
      )}
    </ListItem>
  )
}
